import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Heart, MapPin, UtensilsCrossed, Navigation, ArrowLeft, Trash2 } from "lucide-react";
import { useSavedRestaurants } from "@/hooks/useSavedRestaurants";
import { SEOHead } from "@/components/SEOHead";
import { useMemo } from "react";

const SavedRestaurants = () => {
  const { savedRestaurants, loading, toggleSave } = useSavedRestaurants();

  const grouped = useMemo(() => {
    const map = new Map<string, typeof savedRestaurants>();
    for (const r of savedRestaurants) {
      const list = map.get(r.airport_code) ?? [];
      list.push(r);
      map.set(r.airport_code, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [savedRestaurants]);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Saved Restaurants | Airport Buddy"
        description="Your favorite airport restaurants, organized by airport for quick access on your next trip."
        canonicalUrl="https://airportbuddy.app/saved-restaurants"
      />

      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/dashboard" aria-label="Back to dashboard">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <Heart className="h-5 w-5 text-red-500 fill-red-500 shrink-0" aria-hidden="true" />
            <h1 className="text-lg font-semibold truncate">Saved Restaurants</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-4 rounded-lg border border-border animate-pulse">
                <div className="h-4 w-40 rounded bg-muted skeleton-shimmer mb-2" />
                <div className="h-3 w-24 rounded bg-muted skeleton-shimmer" />
              </div>
            ))}
          </div>
        ) : savedRestaurants.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center space-y-3">
              <UtensilsCrossed className="h-10 w-10 text-muted-foreground mx-auto" aria-hidden="true" />
              <h2 className="text-base font-semibold">No saved restaurants yet</h2>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                Tap the heart icon on any restaurant to save it here for your next trip.
              </p>
              <Button asChild className="mt-2">
                <Link to="/airports">Browse airports</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          grouped.map(([airportCode, items]) => (
            <section key={airportCode} className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Link
                  to={`/airports/${airportCode.toLowerCase()}`}
                  className="inline-flex items-center gap-2 text-sm font-semibold hover:text-primary transition-colors"
                >
                  <MapPin className="h-4 w-4" aria-hidden="true" />
                  {airportCode}
                </Link>
                <span className="text-xs text-muted-foreground">
                  {items.length} {items.length === 1 ? "spot" : "spots"}
                </span>
              </div>

              <Card>
                <CardContent className="p-0 divide-y divide-border/50">
                  {items.map((r) => {
                    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                      `${r.restaurant_name} ${airportCode} airport`
                    )}`;
                    return (
                      <div key={r.id} className="p-4">
                        <div className="flex items-start justify-between gap-2 min-w-0">
                          {r.url ? (
                            <a
                              href={r.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-semibold text-sm hover:text-primary transition-colors truncate min-w-0 flex-1"
                            >
                              {r.restaurant_name}
                            </a>
                          ) : (
                            <p className="font-semibold text-sm truncate min-w-0 flex-1">
                              {r.restaurant_name}
                            </p>
                          )}
                          {r.category && (
                            <Badge variant="secondary" className="text-[10px] shrink-0">
                              {r.category}
                            </Badge>
                          )}
                        </div>

                        {r.terminal && (
                          <p className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
                            <span className="truncate">Terminal {r.terminal}</span>
                          </p>
                        )}

                        <div className="flex items-center justify-between gap-2 mt-3">
                          <a
                            href={directionsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors min-h-[32px]"
                            aria-label={`Get directions to ${r.restaurant_name}`}
                          >
                            <Navigation className="h-3.5 w-3.5" aria-hidden="true" />
                            Get directions
                          </a>
                          <button
                            type="button"
                            onClick={() =>
                              toggleSave({
                                airportCode: r.airport_code,
                                restaurantId: r.restaurant_id,
                                restaurantName: r.restaurant_name,
                                category: r.category,
                                terminal: r.terminal,
                                url: r.url,
                              })
                            }
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-destructive transition-colors min-h-[32px] shrink-0"
                            aria-label={`Remove ${r.restaurant_name} from saved`}
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </section>
          ))
        )}
      </main>
    </div>
  );
};

export default SavedRestaurants;
